import React from "react";
import styled from "styled-components";
import { Divider } from "@material-ui/core";

import { _ } from "js/vendor";
import { AnimationsArrangementType } from "common/constants";
import { getStaticUrl } from "js/config";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap10 } from "js/react/components/Gap";
import { PopupMenu, PopupMenuPaddedContainer } from "js/editor/elementUI/EditorComponents/PopupMenu";

const StyledLabel = styled.label`
    font-weight: 600;
    font-size: 14px;
`;

const ButtonsContainer = styled.div`
    display: grid;
    grid-auto-flow: column;
    gap: 15px;
    height: 70px;
    margin-top: 5px;

    label {
        font-family: "Source Sans Pro";
        font-size: 13px;
        font-weight: 600;
        text-transform: none;
        letter-spacing: 0;
        cursor: pointer;
    }

    img {
        height: 24px;
        margin-bottom: 10px;
    }
`;

const ButtonBox = styled.div.attrs(({ selected, disabled }) => ({
    style: {
        background: selected ? "#eeeeee" : "#ffffff",
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.6 : 1
    }
}))`
    display: flex;
    flex-flow: column;
    align-items: center;
    width: 81px;
    height: 65px;
    padding-top: 5px;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        background: #f0f0f0 !important;
     }
`;

export function AnimationPresetsMenu({ animations, arrangeAnimations, setAnimationsValues, scaleAnimations, arrangementType, disabled }) {
    const animationIds = animations.map(({ id }) => id);
    const enabledAnimationIds = animations.filter(({ disabled }) => !disabled).map(({ id }) => id);
    const animationToArrangeIds = enabledAnimationIds.length > 0 ? enabledAnimationIds : animationIds;

    const averageAnimationsScale = _.meanBy(animations, ({ duration, defaultDuration }) => defaultDuration / duration);
    const currentSpeed = averageAnimationsScale < 0.8 ? "slow" : averageAnimationsScale > 1.2 ? "fast" : "normal";
    const hasWaitForClickAnimations = animations.some(({ waitForClick }) => waitForClick);
    const isAllAnimationsDisabled = !animations.some(({ disabled }) => !disabled);

    return (
        <PopupMenu
            id="animation-presets"
            label={<StyledLabel>AUTO-ARRANGE</StyledLabel>}
            showArrow={true}
            position="below"
            popupWidth={600}
            disabled={disabled}
            preventButtonMouseDownPropagation
        >
            <PopupMenuPaddedContainer onMouseDown={event => event.stopPropagation()}>
                <FlexBox vertical left>
                    <label>Build</label>
                    <ButtonsContainer>
                        <ButtonBox
                            selected={!isAllAnimationsDisabled && arrangementType === AnimationsArrangementType.OVERLAPPING}
                            onClick={event => arrangeAnimations(event, AnimationsArrangementType.OVERLAPPING, animationToArrangeIds, true, true, true)}
                        >
                            <img src={getStaticUrl("/images/ui/animation/animation-overlapping.svg")}></img>
                            <label>Overlapping</label>
                        </ButtonBox>
                        <ButtonBox
                            selected={!isAllAnimationsDisabled && arrangementType === AnimationsArrangementType.SIMULTANEOUS}
                            onClick={event => arrangeAnimations(event, AnimationsArrangementType.SIMULTANEOUS, animationToArrangeIds, true, true, true)}
                        >
                            <img src={getStaticUrl("/images/ui/animation/animation-simultaneous.svg")}></img>
                            <label>Simultaneous</label>
                        </ButtonBox>
                        <ButtonBox
                            selected={!isAllAnimationsDisabled && arrangementType === AnimationsArrangementType.SEQUENTIAL}
                            onClick={event => arrangeAnimations(event, AnimationsArrangementType.SEQUENTIAL, animationToArrangeIds, true, true, true)}
                        >
                            <img src={getStaticUrl("/images/ui/animation/animation-sequential.svg")}></img>
                            <label>Sequential</label>
                        </ButtonBox>
                        <ButtonBox
                            selected={isAllAnimationsDisabled}
                            onClick={event => setAnimationsValues(event, animationIds, { disabled: true })}
                        >
                            <img src={getStaticUrl("/images/ui/animation/animation-none.svg")}></img>
                            <label>No Animation</label>
                        </ButtonBox>
                    </ButtonsContainer>
                    <Gap10 />
                    <Divider />
                    <Gap10 />
                    <label>Speed</label>
                    <ButtonsContainer>
                        <ButtonBox
                            selected={currentSpeed === "slow"}
                            disabled={isAllAnimationsDisabled}
                            onClick={event => scaleAnimations(event, averageAnimationsScale * 2)}
                        >
                            <img src={getStaticUrl("/images/ui/animation/slow.svg")}></img>
                            <label>Slow</label>
                        </ButtonBox>
                        <ButtonBox
                            selected={currentSpeed === "normal"}
                            disabled={isAllAnimationsDisabled}
                            onClick={event => scaleAnimations(event, averageAnimationsScale)}
                        >
                            <img src={getStaticUrl("/images/ui/animation/normal.svg")}></img>
                            <label>Normal</label>
                        </ButtonBox>
                        <ButtonBox
                            selected={currentSpeed === "fast"}
                            disabled={isAllAnimationsDisabled}
                            onClick={event => scaleAnimations(event, averageAnimationsScale / 2)}
                        >
                            <img src={getStaticUrl("/images/ui/animation/fast.svg")}></img>
                            <label>Fast</label>
                        </ButtonBox>
                    </ButtonsContainer>
                    <Gap10 />
                    <Divider />
                    <Gap10 />
                    <label>Advance</label>
                    <ButtonsContainer>
                        <ButtonBox
                            selected={hasWaitForClickAnimations}
                            disabled={isAllAnimationsDisabled}
                            onClick={event => setAnimationsValues(event, animationIds, { waitForClick: true })}
                        >
                            <img src={getStaticUrl("/images/ui/animation/mouse.svg")}></img>
                            <label>On Click</label>
                        </ButtonBox>
                        <ButtonBox
                            selected={!hasWaitForClickAnimations}
                            disabled={isAllAnimationsDisabled}
                            onClick={event => setAnimationsValues(event, animationIds, { waitForClick: false })}
                        >
                            <img src={getStaticUrl("/images/ui/animation/fast-forward.svg")}></img>
                            <label>Automatically</label>
                        </ButtonBox>
                    </ButtonsContainer>
                </FlexBox>
            </PopupMenuPaddedContainer>
        </PopupMenu >
    );
}
